import type { ContentIdea } from "../../../../shared/types";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { cn } from "../../lib/utils";

interface IdeaCardProps {
  idea: ContentIdea;
  index: number;
  selected: boolean;
  onSelect: (index: number) => void;
}

const platformLabels: Record<string, string> = {
  linkedin: "LinkedIn",
  twitter: "Twitter/X",
  instagram: "Instagram",
  threads: "Threads",
  tiktok: "TikTok",
  youtube: "YouTube",
};

export function IdeaCard({ idea, index, selected, onSelect }: IdeaCardProps) {
  return (
    <div
      className={cn(
        "rounded-xl border-2 bg-white p-5 transition-all",
        selected
          ? "border-brand-600 shadow-md ring-1 ring-brand-200"
          : "border-slate-200 hover:border-slate-300",
      )}
    >
      {/* Header */}
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold text-slate-900">
          {idea.headline}
        </h3>
        <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500">
          #{index + 1}
        </span>
      </div>

      {/* Angle */}
      <p className="mb-4 text-sm leading-relaxed text-slate-600">
        {idea.angle}
      </p>

      {/* Platform badges */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {idea.target_platforms.map((platform) => (
          <Badge key={platform} variant="info">
            {platformLabels[platform] || platform}
          </Badge>
        ))}
      </div>

      {/* Meta row */}
      <div className="mb-4 flex items-center gap-4 text-xs text-slate-500">
        <span>
          <span className="font-medium text-slate-700">Format:</span>{" "}
          {idea.format}
        </span>
        <span>
          <span className="font-medium text-slate-700">Est. engagement:</span>{" "}
          {idea.estimated_engagement}
        </span>
      </div>

      {/* Select button */}
      <Button
        variant={selected ? "primary" : "secondary"}
        size="sm"
        onClick={() => onSelect(index)}
        className="w-full"
      >
        {selected ? "Selected" : "Select this idea"}
      </Button>
    </div>
  );
}
